import { Box, Grid, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import styles from '../../../assets/styles/components/Forms/Form.module.scss';
import CircularLoading from '../../CircularLoading';
import PageHeader from '../../PageHeader';
import {
  buildAdName,
  buildBaseNamingConvention,
  diyStepsV2,
  editDiyStepsV2,
  setAdNameValidationValue,
} from '../../../utils/helpers/DiyHelpers';
import { PrimaryButton } from '../../Buttons';
import LinearProgressBarWithLabel from '../../ProgressBar/LinearProgressBarWithLabel';
import {
  AdsTemplate,
  CreateCampaignForm,
  DiySettings,
} from '../../../types/IDiy';
import { useDispatch, useSelector } from 'react-redux';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { toggleAlert } from '../../../redux/actions';
import { errorMessageParser } from '../../../utils/helpers/ToastHelper';
import { adsProvider, Objectives } from '../../../utils/constants/facebookAds';
import {
  createAdVariantV2,
  createDiyCampaignV2,
  editCampaignV2,
  fetchCampaignTemplates,
  fetchSingleCampaignTemplate,
  getSingleDiySetting,
} from '../../../services/diy';
import DiyTemplateForm from './DiyTemplateForm';
import { DrawerMenu } from '../../Menu';
import { AdsPreviewSquare } from './AdsPreview';
import {
  duplicateFbAdAccountImages,
  fetchBrandCampaigns,
  fetchSingleFbAdAccountVideo,
  fetchSpecificFbAdset,
  fetchSpecificFbCampaign,
  getCampaignByAdId,
  getFbPageDefaultIgAccount,
  getFbTargetingReachEstimate,
  getSingleFbAdCreative,
} from '../../../services/ads';
import {
  AdVideo,
  AudienceSet,
  Brand,
  Campaign,
  CampaignAd,
  CampaignAdSet,
  DemographicsSet,
  FbAssetFeedSpecMedia,
  FbCreativeForm,
  FbTargetSearch,
  IGeolocationSet,
} from '../../../types';
import ModalHeader from '../../Modal/ModalHeader';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DiyGeolocationSetForm from './DiyGeolocationSetForm';
import {
  FBBillingEvent,
  FBDestinationType,
  FBOptimizationGoal,
} from '../../../utils/helpers/facebookAdsHelper';
import DiyAudienceSetForm from './DiyAudienceSetForm';
import DiyDemographicsSetForm from './DiyDemographicsSetForm';
import DiyBudgetFormV2 from './DiyBudgetFormV2';
import { dayJsInstance } from '../../../utils/dayjsHelper';
import DiyAdPreviewV2 from './DiyAdPreviewV2';
import DiyAdDetailsFormV2 from './DiyAdDetailsFormV2';
import { orderArrayOfObject } from '../../../utils/arrayFormatter';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  getBrandAudienceSets,
  getBrandDemographicsSets,
  getBrandLocationSets,
  getSingleBrandAudienceSets,
  getSingleBrandDemographicsSets,
  getSingleBrandLocationSets,
} from '../../../services/assets';
import { parsedDetailedTargeting } from '../../../utils/helpers/AssetHelpers';
import { getSingleAdBudget } from '../../../services/budget';
import DiyBillingSummaryV2 from './DiyBillingSummaryV2';
import TemplateForm from './TemplateForm';
import { ScrollStyle, TIMEZONE_EASTERN } from '../../../utils';
import GeolocationSetForm from '../Assetz/Facebook/GeolocationSet/GeolocationSetForm';
import AudienceSetForm from '../Assetz/Facebook/Audience/AudienceSetForm';
import DemographicsSetForm from '../Assetz/Facebook/DemographicsSet/DemographicsSetForm';
import { useForm } from 'react-hook-form';
import { buildBrandAddress } from '../../../utils/helpers/BrandHelpers';

const DiyFormV2: FC = () => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
    setValue,
  } = useForm({ mode: 'onSubmit' });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));
  const brand: Brand = useSelector((state: any) => state?.brand?.brand);
  const location: Brand = useSelector(
    (state: any) => state?.location?.location,
  );
  const timezone: string = useSelector(
    (state: any) => state?.brandTimezone?.timezone,
  );
  // Edit ad params
  const params = useParams();
  const adId: string = params.adId;
  const [searchParams] = useSearchParams();
  const fields: string[] = searchParams.get('fields')?.split(',');
  const isRestart: boolean = searchParams.get('restart') === 'true';
  const isTemplateChanged = fields?.includes('template');
  const isBudgetChanged = fields?.includes('budget');
  const isAudienceChanged = fields?.includes('audience');
  const isGeographicsChanged = fields?.includes('geographics');
  const isFormChanged = fields?.includes('form');
  const isWebsiteUrlChanged = fields?.includes('website');
  const isAdNameChanged = fields?.includes('campaign_details');
  const pathLocation = useLocation();
  const pathNames = pathLocation.pathname.split('/');
  const isEdit = pathNames.includes('edit');
  const isAbTest = pathNames.includes('ab-test');

  const [steps, setSteps] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<string>(steps[0]);
  const [progress, setProgress] = useState<number>(0);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [formValues, setFormValues] = useState<CreateCampaignForm>({
    paymentMethod: null,
    limited: false,
    campaignId: '',
    campaignTemplate: {
      name: '',
      baseName: '',
      status: 'ACTIVE',
      objective: Objectives.OUTCOME_LEADS,
      special_ad_categories: [],
      special_ad_category_country: [],
      account_id: '',
      tag: '',
      budgetLevel: 'adset',
      // Only set if campaign level budget
      daily_budget: 0,
      lifetime_budget: null,
      bid_strategy: null,
      totalBudget: 0,
      futureBudget: 0,
      futureDailyBudget: 0,
      confirmPayment: false,
      totalCharge: 0,
    },
    adSetTemplate: {
      name: '',
      baseName: '',
      billing_event: FBBillingEvent.Impressions,
      status: 'ACTIVE',
      optimization_goal: FBOptimizationGoal.LeadGeneration,
      destination_type: FBDestinationType.OnAd,
      promoted_object: {
        page_id: '',
      },
      tag: '',
      geolocationSet: '',
      audienceSet: '',
      demographicsSet: '',
      start_time: dayJsInstance()
        ?.tz(timezone || TIMEZONE_EASTERN)
        ?.unix(),
      // Only set if adset level budget
      bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
      daily_budget: 0,
      lifetime_budget: null,
      totalBudget: 0,
      futureBudget: 0,
      futureDailyBudget: 0,
      confirmPayment: false,
      totalCharge: 0,
    },
    adTemplate: {
      name: '',
      baseName: '',
      status: 'ACTIVE',
      template: '',
      tag: '',
      leadAdTool: 'instant_form',
    },
  });
  const [selectedTemplate, setSelectedTemplate] = useState<AdsTemplate>(null);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [diySettings, setDiySettings] = useState<DiySettings>(null);
  const [showMobilePreview, setShowMobilePreview] = useState<boolean>(false);
  const [adVideo, setAdVideo] = useState<AdVideo>(null);
  const [adVideoLoading, setAdVideoLoading] = useState<boolean>(false);
  const [selectedGeolocationSet, setSelectedGeolocationSet] =
    useState<IGeolocationSet>(null);
  const [reachEstimate, setReachEstimate] = useState<any>({
    users_lower_bound: 0,
    users_upper_bound: 0,
  });
  const [reachError, setReachError] = useState<string>(null);
  const [reachLoading, setReachLoading] = useState<boolean>(false);
  const [selectedAudienceSet, setSelectedAudienceSet] =
    useState<AudienceSet>(null);
  const [selectedDemographicsSet, setSelectedDemographicsSet] =
    useState<DemographicsSet>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [ad, setAd] = useState<CampaignAd>(null);
  const [currentMonthlyBudget, setCurrentTotalBudget] = useState<number>(0);
  const [confirmPayment, setConfirmPayment] = useState<boolean>(false);
  const [rebuildLoading, setRebuildLoading] = useState<boolean>(false);
  const [nameBuilt, setNameBuilt] = useState<boolean>(false);
  const [openTemplateForm, setOpenTemplateForm] = useState<boolean>(false);
  const [templateAction, setTemplateAction] = useState<
    'create' | 'edit' | 'duplicate'
  >('create');
  const [templates, setTemplates] = useState<AdsTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<AdsTemplate[]>([]);
  const [geolocationSets, setGeolocationSets] = useState<IGeolocationSet[]>([]);
  const [filteredGeolocationSets, setFilteredGeolocationSets] = useState<
    IGeolocationSet[]
  >([]);
  const [openGeolocationSetForm, setOpenGeolocationSetForm] =
    useState<boolean>(false);
  const [geolocationSetAction, setGeolocationSetAction] = useState<
    'create' | 'edit'
  >('create');
  const [audienceSets, setAudienceSets] = useState<AudienceSet[]>([]);
  const [filteredAudienceSets, setFilteredAudienceSets] = useState<
    AudienceSet[]
  >([]);
  const [openAudienceSetForm, setOpenAudienceSetForm] =
    useState<boolean>(false);
  const [audienceSetAction, setAudienceSetAction] = useState<'create' | 'edit'>(
    'create',
  );
  const [demographicsSets, setDemographicsSets] = useState<DemographicsSet[]>(
    [],
  );
  const [filteredDemographicsSets, setFilteredDemographicsSets] = useState<
    DemographicsSet[]
  >([]);
  const [openDemographicsSetForm, setOpenDemographicsSetForm] =
    useState<boolean>(false);
  const [demographicsSetAction, setDemographicsSetAction] = useState<
    'create' | 'edit'
  >('create');
  const [geolocationSetsLoading, setGeolocationSetsLoading] =
    useState<boolean>(false);
  const [audienceSetsLoading, setAudienceSetsLoading] =
    useState<boolean>(false);
  const [parsedBody, setParsedBody] = useState<string>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>(null);
  const [locationSetInitialLoad, setLocationSetInitialLoad] =
    useState<boolean>(false);
  const [audienceSetInitialLoad, setAudienceSetInitialLoad] =
    useState<boolean>(false);
  const [demographicsSetInitialLoad, setDemographicsSetInitialLoad] =
    useState<boolean>(false);
  const [stepsInitialized, setStepsInitialized] = useState<boolean>(false);

  useEffect(() => {
    const temp =
      isEdit || isAbTest
        ? editDiyStepsV2({
            brand: location || brand,
            isTemplateChanged: isTemplateChanged,
            isBudgetChanged,
            isAudienceChanged,
            isGeographicsChanged,
            isLeadToolChanged: isFormChanged || isWebsiteUrlChanged,
            isAdNameChanged,
            isAbTest,
          })
        : diyStepsV2(location || brand);

    setSteps(temp);
    if (!stepsInitialized) {
      setProgress((1 / temp.length) * 100);
      setActiveStep(temp[0]);
    }
    setStepsInitialized(true);
  }, [
    stepsInitialized,
    isEdit,
    isAbTest,
    location,
    brand,
    isTemplateChanged,
    isBudgetChanged,
    isAudienceChanged,
    isGeographicsChanged,
    isFormChanged,
    isWebsiteUrlChanged,
  ]);

  useEffect(() => {
    if (selectedTemplate?.media === 'video') {
      setAdVideo(null);
      getVideo();
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (selectedTemplate && !isEdit) {
      setValue(
        'callToAction',
        selectedTemplate?.creativeTemplate?.asset_feed_spec
          ?.call_to_actions?.[0]?.type,
      );
    }
  }, [selectedTemplate, isEdit]);

  useEffect(() => {
    if (location || brand) {
      getDiySettings();
    }
  }, [location, brand]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getFacebookTargetReach(formValues?.adSetTemplate?.targeting);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [formValues?.adSetTemplate?.targeting]);

  useEffect(() => {
    if (activeStep === 'Campaign Details' && !nameBuilt && !isEdit) {
      let temp: CreateCampaignForm = { ...formValues };

      const campaignBaseName = buildBaseNamingConvention({
        formValues: temp,
        level: 'campaign',
        brand: location || brand,
        campaigns,
        limited: temp?.limited,
      });

      const adsetBaseName = buildBaseNamingConvention({
        formValues: temp,
        level: 'adset',
        brand: location || brand,
        campaigns,
        limited: temp?.limited,
        campaignName: campaignBaseName,
        campaignTag: temp?.campaignTemplate?.tag,
      });

      const adBaseName = buildBaseNamingConvention({
        formValues: temp,
        level: 'ad',
        brand: location || brand,
        campaigns,
        limited: temp?.limited,
        adSetName: adsetBaseName,
        adSetTag: temp?.adSetTemplate?.tag,
      });

      temp = {
        ...temp,
        campaignTemplate: {
          ...temp?.campaignTemplate,
          baseName: campaignBaseName,
        },
        adSetTemplate: {
          ...temp?.adSetTemplate,
          baseName: adsetBaseName,
        },
        adTemplate: {
          ...temp?.adTemplate,
          baseName: adBaseName,
        },
      };

      if (campaigns.length > 0) {
        let tempCampaigns: Campaign[] = [];
        if (temp?.campaignTemplate?.objective) {
          tempCampaigns = campaigns?.filter(
            (campaign: Campaign) =>
              campaign.objective === temp?.campaignTemplate?.objective,
          );
        } else {
          tempCampaigns = campaigns;
        }

        if (tempCampaigns?.length > 0) {
          temp = {
            ...temp,
            campaignId: tempCampaigns[0]?.id,
            campaignTemplate: {
              ...temp?.campaignTemplate,
              budgetLevel: tempCampaigns[0]?.budgetLevel || 'adset',
            },
            limited: tempCampaigns[0]?.limited || false,
          };

          setSelectedCampaign(tempCampaigns[0]);
        } else {
          temp = {
            ...temp,
            campaignId: '',
            campaignTemplate: {
              ...temp?.campaignTemplate,
              budgetLevel: 'adset',
            },
            limited: false,
          };

          setSelectedCampaign(null);
        }
      }

      setAdNameValidationValue(temp, setValue);
      setFormValues(temp);
      setNameBuilt(true);
    }
  }, [nameBuilt, activeStep, isEdit, campaigns]);

  useEffect(() => {
    if (location || brand) {
      getCampaigns();
    }
  }, [location, brand]);

  useEffect(() => {
    if ((isEdit || isAbTest) && adId && (location || brand)) {
      rebuildEditForm();
    }
  }, [adId, isEdit, isAbTest, location, brand]);

  useEffect(() => {
    if (location || brand) {
      if (activeStep === 'Templates') {
        getTemplates();
      }

      if (activeStep === 'Geolocations') {
        getGeolocationSets();
      }

      if (activeStep === 'Audiences') {
        getAudienceSets();
      }

      if (activeStep === 'Demographics') {
        getDemographicsSets();
      }
    }
  }, [location, brand, activeStep]);

  const getTemplates = async () => {
    try {
      setLoading(true);

      const response = await fetchCampaignTemplates(
        brand?.__type === 'location' ? brand?.franchisor?._id : brand?._id,
        adsProvider.FACEBOOK,
        true,
      );

      setTemplates(response.data);
      setFilteredTemplates(response.data);
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getGeolocationSets = async () => {
    try {
      setGeolocationSetsLoading(true);

      const response = await getBrandLocationSets({
        brandId: (location || brand)?._id,
        archived: false,
      });

      const primaryGeolocationSet = response.data?.find(
        (geolocationSet: IGeolocationSet) => geolocationSet.primary,
      );

      if (primaryGeolocationSet && !selectedGeolocationSet && !isEdit) {
        handleSelectGeolocationSet(primaryGeolocationSet);
      }

      const sortedResponse = response.data.sort((a: IGeolocationSet) => {
        if (a.primary) return -1;

        return 1;
      });

      setGeolocationSets(sortedResponse);
      setFilteredGeolocationSets(sortedResponse);
    } catch (error) {
      console.log(error);
    } finally {
      setGeolocationSetsLoading(false);
    }
  };

  const getAudienceSets = async () => {
    try {
      setAudienceSetsLoading(true);

      const response = await getBrandAudienceSets({
        brandId: brand?._id,
        archived: false,
      });

      const primaryAudienceSet = response.data.find(
        (audienceSet: AudienceSet) => audienceSet.primary,
      );

      if (primaryAudienceSet && !selectedAudienceSet && !isEdit) {
        handleSelectAudienceSet(primaryAudienceSet);
      }

      const sortedResponse = response.data.sort((a: AudienceSet) => {
        if (a.primary) return -1;

        return 1;
      });

      setAudienceSets(sortedResponse);
      setFilteredAudienceSets(sortedResponse);
    } catch (error) {
    } finally {
      setAudienceSetsLoading(false);
    }
  };

  const getDemographicsSets = async () => {
    try {
      setLoading(true);

      const response = await getBrandDemographicsSets({
        brandId: brand?._id,
        archived: false,
      });

      const primaryDemographicsSet = response.data.find(
        (demographicsSet: DemographicsSet) => demographicsSet.primary,
      );

      if (primaryDemographicsSet && !selectedDemographicsSet && !isEdit) {
        handleSelectDemographicsSet(primaryDemographicsSet);
      }

      const sortedResponse = response.data.sort((a: DemographicsSet) => {
        if (a.primary) return -1;

        return 1;
      });

      setDemographicsSets(sortedResponse);
      setFilteredDemographicsSets(sortedResponse);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getDiySettings = async () => {
    try {
      setFetchLoading(true);

      const response = await getSingleDiySetting((location || brand)?._id);

      setDiySettings(response.data);
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setFetchLoading(false);
    }
  };

  const getVideo = async () => {
    try {
      setAdVideoLoading(true);

      const response = await fetchSingleFbAdAccountVideo(
        brand?._id,
        selectedTemplate?.creativeTemplate?.asset_feed_spec?.videos[0]
          ?.video_id,
      );

      setAdVideo(response.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setAdVideoLoading(false);
    }
  };

  const getFacebookTargetReach = async (targeting: FbTargetSearch) => {
    try {
      setReachLoading(true);

      const response = await getFbTargetingReachEstimate({
        brandId: brand?._id,
        targets: targeting,
        optimizationGoal:
          formValues?.campaignTemplate?.objective === Objectives.OUTCOME_TRAFFIC
            ? FBOptimizationGoal.LinkClicks
            : FBOptimizationGoal.LeadGeneration,
      });

      setReachEstimate(response.data);
      setReachError(null);
    } catch (error: any) {
      setReachEstimate({
        users_lower_bound: 0,
        users_upper_bound: 0,
      });
      const err = error.response.data;
      setReachError(err?.message);
    } finally {
      setReachLoading(false);

      if (isEdit) {
        if (isAudienceChanged) {
          setAudienceSetInitialLoad(true);
          setDemographicsSetInitialLoad(true);
        }

        if (isGeographicsChanged) {
          setLocationSetInitialLoad(true);
        }
      } else {
        if (activeStep === 'Geolocations') {
          setLocationSetInitialLoad(true);
        } else if (activeStep === 'Audiences') {
          setAudienceSetInitialLoad(true);
        } else if (activeStep === 'Demographics') {
          setDemographicsSetInitialLoad(true);
        }
      }
    }
  };

  const getCampaigns = async () => {
    try {
      setFetchLoading(true);

      const response = await fetchBrandCampaigns(brand?._id);

      const filteredCampaigns = response.data.filter(
        (campaign: Campaign) =>
          campaign.objective === formValues?.campaignTemplate?.objective,
      );

      const orderedCampaigns = orderArrayOfObject(
        filteredCampaigns,
        'createdAt',
        'desc',
      );

      setFormValues({ ...formValues, campaignId: orderedCampaigns[0]?.id });
      setCampaigns(orderedCampaigns);
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const getPageDefaultIgAccount = async () => {
    try {
      const response = await getFbPageDefaultIgAccount(
        diySettings?.facebookPage?.fbPageId,
        (location || brand)?._id,
      );

      return response.data;
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);
      console.log(errorMsg);
      return { error: 'Please setup your Instagram Account first.' };
    }
  };

  const rebuildEditForm = async () => {
    try {
      setRebuildLoading(true);

      const response = await getCampaignByAdId(adId, (location || brand)?._id);

      const campaign: Campaign = response.data;
      setSelectedCampaign(campaign);

      const adObj: CampaignAd = campaign?.ads?.find(
        (ad: CampaignAd) => ad.id === adId,
      );
      setAd(adObj);

      const adSet: CampaignAdSet = campaign?.adSets?.find(
        (adset: CampaignAdSet) => adset.id === adObj?.adset_id,
      );

      const isCampaignLevelBudget = campaign.budgetLevel === 'campaign';

      let geolocationSetData: IGeolocationSet = {};
      try {
        const geolocationSet = await getSingleBrandLocationSets(
          (location || brand)?._id,
          adSet?.geolocationSet,
        );
        geolocationSetData = geolocationSet?.data;
        setSelectedGeolocationSet(geolocationSetData);
      } catch (error: any) {
        console.log(error);
      }

      let targets: any = {};
      try {
        const audienceSet = await getSingleBrandAudienceSets(
          (location || brand)?._id,
          adSet?.audienceSet,
        );
        const audienceSetData: AudienceSet = audienceSet?.data;
        setSelectedAudienceSet(audienceSetData);
        targets = parsedDetailedTargeting(audienceSetData?.targets);
      } catch (error: any) {
        console.log(error);
      }

      let demographicsSetData: DemographicsSet = {};
      try {
        const demographicsSet = await getSingleBrandDemographicsSets(
          (location || brand)?._id,
          adSet?.demographicsSet,
        );
        demographicsSetData = demographicsSet?.data;
        setSelectedDemographicsSet(demographicsSet?.data);
      } catch (error: any) {
        console.log(error);
      }

      const template = await fetchSingleCampaignTemplate(adObj?.template);
      setSelectedTemplate(template.data);

      let targeting: any = {
        ...targets,
        geo_locations: geolocationSetData?.geolocations || {},
        excluded_geo_locations: geolocationSetData?.excludedGeolocations || {},
        genders: demographicsSetData?.genders,
        age_min: demographicsSetData?.age_min,
        age_max: demographicsSetData?.age_max,
      };

      const currentDate = dayJsInstance().tz(timezone);
      const currentMonth = currentDate.clone().month() + 1;
      const currentYear = currentDate.clone().year();
      let futureMonth = currentMonth + 1;
      let futureYear = currentYear;
      if (futureMonth > 12) {
        futureMonth = 1;
        futureYear = futureYear + 1;
      }
      let budgetParams: any = {
        brandId: (location || brand)?._id,
        month: currentMonth,
        year: currentYear,
        provider: adsProvider.FACEBOOK,
      };

      if (isCampaignLevelBudget) {
        budgetParams.campaignId = campaign?._id;
      } else {
        budgetParams.adsetId = adSet.id;
      }

      const currentBudget = await getSingleAdBudget(budgetParams);

      setCurrentTotalBudget(currentBudget?.data?.totalBudget || 0);

      const futureBudget = await getSingleAdBudget({
        ...budgetParams,
        month: futureMonth,
        year: futureYear,
      });

      let fbCampaign: any;
      if (isCampaignLevelBudget) {
        fbCampaign = await fetchSpecificFbCampaign(
          campaign.id,
          (location || brand)?._id,
        );
      }

      let fbAdset: any;
      if (!isCampaignLevelBudget) {
        fbAdset = await fetchSpecificFbAdset(
          adSet.id,
          (location || brand)?._id,
        );
      }

      let currentCreativeTempalte: FbCreativeForm = {};
      try {
        const fbCreative = await getSingleFbAdCreative(
          (location || brand)?._id,
          adObj?.creative.creative_id,
        );

        const creativeData: FbCreativeForm = fbCreative?.data;

        currentCreativeTempalte = {
          asset_feed_spec: {
            images: creativeData?.asset_feed_spec?.images || [],
            videos: creativeData?.asset_feed_spec?.videos || [],
            ad_formats: creativeData?.asset_feed_spec?.ad_formats,
            bodies: creativeData?.asset_feed_spec?.bodies,
            titles: creativeData?.asset_feed_spec?.titles,
            descriptions: creativeData?.asset_feed_spec?.descriptions,
            call_to_action_types:
              creativeData?.asset_feed_spec?.call_to_action_types,
            link_urls: creativeData?.asset_feed_spec?.link_urls,
            call_to_actions: creativeData?.asset_feed_spec?.call_to_actions,
            asset_customization_rules: creativeData?.asset_feed_spec
              ?.asset_customization_rules || [
              {
                customization_spec: {
                  publisher_platforms: ['facebook', 'instagram'],
                  facebook_positions: [
                    'feed',
                    'video_feeds',
                    'marketplace',
                    'search',
                  ],
                  instagram_positions: [
                    'stream',
                    'ig_search',
                    'explore',
                    'explore_home',
                    'profile_feed',
                  ],
                },
                image_label: {
                  name:
                    creativeData?.asset_feed_spec?.ad_formats[0] ===
                    'SINGLE_IMAGE'
                      ? 'image_square'
                      : 'video_square',
                },
              },
              {
                customization_spec: {
                  publisher_platforms: [
                    'facebook',
                    'instagram',
                    'audience_network',
                    'messenger',
                  ],
                  facebook_positions: ['facebook_reels', 'story'],
                  instagram_positions: ['story', 'reels'],
                  messenger_positions: ['story'],
                },
                image_label: {
                  name:
                    creativeData?.asset_feed_spec?.ad_formats[0] ===
                    'SINGLE_IMAGE'
                      ? 'image_vertical'
                      : 'video_vertical',
                },
              },
            ],
          },
          degrees_of_freedom_spec: creativeData?.degrees_of_freedom_spec,
          object_story_spec: creativeData?.object_story_spec,
        };
      } catch (error: any) {
        currentCreativeTempalte = { ...adObj?.creativeTemplate };
      }

      const creativeParams: FbCreativeForm = currentCreativeTempalte;

      setValue(
        'callToAction',
        creativeParams?.asset_feed_spec?.call_to_actions?.[0]?.type,
      );

      if (campaign?.objective === Objectives.OUTCOME_LEADS) {
        setValue(
          'leadGenForm',
          creativeParams?.asset_feed_spec?.call_to_actions?.[0]?.value
            ?.lead_gen_form_id,
        );
      } else {
        setValue(
          'link',
          creativeParams?.asset_feed_spec?.call_to_actions?.[0]?.value?.link,
        );
      }

      setValue('description', adObj?.description);
      setValue(
        'campaignName',
        buildAdName(campaign?.baseName || campaign?.name, campaign?.tag),
      );
      setValue(
        'adsetName',
        buildAdName(adSet?.baseName || adSet?.name, adSet?.tag),
      );
      setValue(
        'adName',
        buildAdName(adObj?.baseName || adObj?.name, adObj?.tag),
      );

      setFormValues({
        limited: campaign?.limited,
        campaignId: campaign?.id,
        campaignTemplate: {
          name: campaign?.name,
          baseName: campaign?.baseName || campaign?.name,
          objective: campaign?.objective,
          status: campaign?.status,
          special_ad_categories: campaign?.special_ad_categories,
          special_ad_category_country: campaign?.special_ad_category_country,
          account_id: campaign?.accountId,
          tag: campaign?.tag,
          totalBudget:
            isCampaignLevelBudget && !isRestart
              ? currentBudget?.data?.totalBudget || 0
              : 0,
          futureBudget: isCampaignLevelBudget
            ? futureBudget?.data?.totalBudget || 0
            : 0,
          totalCharge: isCampaignLevelBudget
            ? currentBudget?.data?.totalCharge ||
              0 - currentBudget?.data?.totalCharge ||
              0
            : 0,
          bid_strategy: isCampaignLevelBudget ? campaign.bid_strategy : null,
          daily_budget:
            isCampaignLevelBudget && !campaign.limited
              ? (fbCampaign?.data?.daily_budget || 0) / 100
              : null,
          lifetime_budget:
            isCampaignLevelBudget && campaign.limited
              ? (fbCampaign?.data?.lifetime_budget || 0) / 100
              : null,
          budgetLevel: campaign?.budgetLevel || 'adset',
          confirmPayment: false,
        },
        adSetTemplate: {
          name: adSet?.name,
          baseName: adSet?.baseName || adSet?.name,
          daily_budget:
            !isCampaignLevelBudget && !campaign.limited
              ? (fbAdset?.data?.daily_budget || 0) / 100
              : null,
          billing_event: adSet?.billing_event,
          status: adSet?.status,
          lifetime_budget:
            !isCampaignLevelBudget && campaign.limited
              ? (fbAdset?.data?.lifetime_budget || 0) / 100
              : null,
          optimization_goal: adSet?.optimization_goal,
          bid_strategy: adSet?.bid_strategy,
          destination_type: adSet?.destination_type,
          targeting,
          start_time: adSet?.start_time,
          end_time: adSet?.end_time,
          promoted_object: adSet?.promoted_object,
          tag: adSet?.tag,
          geolocationSet: adSet?.geolocationSet,
          audienceSet: adSet?.audienceSet,
          demographicsSet: adSet?.demographicsSet,
          totalBudget:
            !isCampaignLevelBudget && !isRestart
              ? currentBudget?.data?.totalBudget || 0
              : 0,
          futureBudget: !isCampaignLevelBudget
            ? futureBudget?.data?.totalBudget || 0
            : 0,
          totalCharge: !isCampaignLevelBudget
            ? currentBudget?.data?.totalCharge ||
              0 - currentBudget?.data?.totalCharge ||
              0
            : 0,
          confirmPayment: false,
        },
        adTemplate: {
          name: adObj?.name,
          baseName: adObj?.baseName || adObj?.name,
          status: adObj?.status,
          template: adObj?.template,
          tag: isAbTest ? '' : adObj?.tag,
          leadAdTool: adObj?.leadAdTool,
          description: adObj?.description,
        },
        creativeTemplate: creativeParams,
        isBudgetChanged,
        isTargetChanged: isAudienceChanged || isGeographicsChanged,
        isTemplateChanged,
        isAdNameChanged,
        isCreativeChanged:
          isWebsiteUrlChanged || isFormChanged || isTemplateChanged,
        isAudienceChanged,
        isFormChanged,
        isWebsiteUrlChanged,
        isGeographicsChanged,
      });
    } catch (error: any) {
      console.log(error);
    } finally {
      setRebuildLoading(false);
    }
  };

  const renderBudgetError = () => {
    let error: string = '';
    const isCampaignLevelBudget =
      formValues?.campaignTemplate?.budgetLevel === 'campaign';

    if (
      (isCampaignLevelBudget
        ? formValues?.campaignTemplate
        : formValues?.adSetTemplate
      )?.totalBudget < 1 &&
      (!isCampaignLevelBudget ||
        (isCampaignLevelBudget && !formValues?.campaignId))
    ) {
      error = 'Total budget must be greater than $0.00';
    }

    if (
      (isCampaignLevelBudget
        ? formValues?.campaignTemplate
        : formValues?.adSetTemplate
      )?.daily_budget < 1 &&
      !formValues?.limited &&
      (!isCampaignLevelBudget ||
        (isCampaignLevelBudget && !formValues?.campaignId))
    ) {
      error = 'Daily budget must be at least $1.00';
    }

    if (
      (isCampaignLevelBudget
        ? formValues?.campaignTemplate
        : formValues?.adSetTemplate
      )?.futureBudget < 31 &&
      (!isCampaignLevelBudget ||
        (isCampaignLevelBudget && !formValues?.campaignId))
    ) {
      error = 'Future budget must be at least $31.00';
    }

    return error;
  };

  const handleOnSubmit = async () => {
    try {
      const errorMsg = renderError();

      if (!!errorMsg) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message: errorMsg,
            type: 'error',
          }),
        );
      }

      setLoading(true);

      let instagramId: string = '';

      if (!diySettings?.instagramAccount?._id) {
        if (diySettings?.facebookPage?.instagramId) {
          instagramId = diySettings?.facebookPage?.instagramId;
        } else {
          const response = await getPageDefaultIgAccount();
          if (response.error)
            return dispatch(
              toggleAlert({
                toggle: true,
                message: response.error,
                type: 'error',
              }),
            );

          instagramId = response.id;
        }
      } else {
        instagramId = diySettings?.instagramAccount?.instagramId;
      }

      const isCampaignLevelBudget =
        formValues?.campaignTemplate?.budgetLevel === 'campaign';

      let params: CreateCampaignForm = {
        ...formValues,
        campaignTemplate: {
          ...formValues?.campaignTemplate,
          name: buildAdName(
            formValues?.campaignTemplate?.baseName,
            formValues?.campaignTemplate?.tag,
          ),
          bid_strategy: isCampaignLevelBudget
            ? 'LOWEST_COST_WITHOUT_CAP'
            : null,
        },
        adSetTemplate: {
          ...formValues?.adSetTemplate,
          name: buildAdName(
            formValues?.adSetTemplate?.baseName,
            formValues?.adSetTemplate?.tag,
          ),
          bid_strategy: !isCampaignLevelBudget
            ? 'LOWEST_COST_WITHOUT_CAP'
            : null,
          promoted_object: {
            ...formValues?.adSetTemplate?.promoted_object,
            page_id: diySettings?.facebookPage?.fbPageId,
          },
          targeting: {
            ...formValues?.adSetTemplate?.targeting,
            targeting_optimization:
              formValues?.campaignTemplate?.objective ===
              Objectives.OUTCOME_TRAFFIC
                ? null
                : formValues?.adSetTemplate?.targeting?.targeting_optimization,
          },
        },
        adTemplate: {
          ...formValues?.adTemplate,
          name: buildAdName(
            formValues?.adTemplate?.baseName,
            formValues?.adTemplate?.tag,
          ),
        },
        creativeTemplate: {
          ...formValues?.creativeTemplate,
          object_story_spec: {
            ...formValues?.creativeTemplate?.object_story_spec,
            page_id: diySettings?.facebookPage?.fbPageId,
            instagram_actor_id: instagramId,
          },
          asset_feed_spec: {
            ...formValues?.creativeTemplate?.asset_feed_spec,
            images:
              formValues?.creativeTemplate?.asset_feed_spec?.images?.length > 0
                ? formValues?.creativeTemplate?.asset_feed_spec?.images?.map(
                    (image: FbAssetFeedSpecMedia) => {
                      return {
                        adlabels: image?.adlabels,
                        hash: image?.hash,
                      };
                    },
                  )
                : [],
            link_urls: formValues?.creativeTemplate?.asset_feed_spec
              ?.link_urls?.[0]?.website_url
              ? formValues?.creativeTemplate?.asset_feed_spec?.link_urls
              : [
                  {
                    display_url: diySettings?.defaultUrl,
                    website_url: diySettings?.defaultUrl,
                  },
                ],
          },
          degrees_of_freedom_spec: {
            creative_features_spec: {
              standard_enhancements: {
                enroll_status: 'OPT_OUT',
              },
            },
          },
          contextual_multi_ads: {
            enroll_status: 'OPT_OUT',
          },
        },
      };

      /**
       * Image/Video Duplicator
       * @description Duplicate media to the different ad account if the template
       *              was created using different ad account.
       */
      if (selectedTemplate?.accountId !== diySettings?.fbAdAccount?.accountId) {
        if (selectedTemplate?.media === 'image') {
          params?.creativeTemplate?.asset_feed_spec?.images?.forEach(
            async (image: FbAssetFeedSpecMedia) => {
              const data: any = {
                source_account_id: selectedTemplate?.accountId,
                hash: image?.hash,
              };

              await duplicateFbAdAccountImages((location || brand)?._id, data);
            },
          );
        } else {
          return dispatch(
            toggleAlert({
              toggle: true,
              message:
                'The template was created using different ad account. Try to use a different one or update your template.',
              type: 'error',
            }),
          );
        }
      }

      let redirectUrl: string = '/adz';
      if (isAbTest) {
        await createAdVariantV2({
          params,
          brandId: (location || brand)?._id,
          adId,
        });
      } else if (isEdit) {
        const response = await editCampaignV2(adId, params, isRestart);

        const budgetChanged =
          ((isBudgetChanged && (location || brand)?.allowSetupPayment) ||
            response?.data?.budgetChanged) &&
          (isCampaignLevelBudget
            ? params?.campaignTemplate
            : params?.adSetTemplate
          )?.totalCharge > 0;
        const creditAmount = response?.data?.totalCredit || 0;
        const thisMonthsBudget = response?.data?.thisMonthsBudget || 0;

        redirectUrl = `${redirectUrl}${
          budgetChanged ? '?fromUpdateBudget=true' : ''
        }${
          budgetChanged && creditAmount > 0
            ? `&creditAmount=${response.data.totalCredit}`
            : ''
        }${
          budgetChanged && thisMonthsBudget > 0
            ? `&thisMonthsBudget=${thisMonthsBudget}`
            : ''
        }`;
      } else {
        await createDiyCampaignV2((location || brand)?._id, params);
      }

      dispatch(
        toggleAlert({
          toggle: true,
          message: getSuccessMessage(),
        }),
      );

      navigate(redirectUrl);
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const showNextButton = () => {
    if (activeStep === 'Templates') {
      return !!formValues?.adTemplate?.template;
    }

    if (activeStep === 'Geolocations') {
      return !!formValues?.adSetTemplate?.geolocationSet;
    }

    if (activeStep === 'Audiences') {
      return !!formValues?.adSetTemplate?.audienceSet;
    }

    if (activeStep === 'Demographics') {
      return !!formValues?.adSetTemplate?.demographicsSet;
    }

    if (activeStep === 'Billing Summary') {
      const isCampaignLevelBudget =
        formValues?.campaignTemplate?.budgetLevel === 'campaign';
      const isIncrease =
        (isCampaignLevelBudget
          ? formValues?.campaignTemplate
          : formValues?.adSetTemplate
        )?.totalBudget > (!isRestart ? currentMonthlyBudget : 0);
      const isPaymentAllowed = (location || brand)?.allowSetupPayment;

      return (
        (confirmPayment &&
          isIncrease &&
          (!isPaymentAllowed ||
            (isPaymentAllowed && !!formValues?.paymentMethod))) ||
        (isCampaignLevelBudget
          ? formValues?.campaignTemplate
          : formValues?.adSetTemplate
        )?.totalCharge === 0 ||
        !isIncrease
      );
    }

    return true;
  };

  const renderError = () => {
    if (['Geolocations', 'Audiences', 'Demographics'].includes(activeStep)) {
      if (reachError) return reachError;

      const isAIEnabled =
        selectedAudienceSet?.targets?.targeting_automation
          ?.advantage_audience === 1;

      if (
        activeStep === 'Demographics' &&
        isAIEnabled &&
        (selectedDemographicsSet?.age_min > 25 ||
          selectedDemographicsSet?.age_max < 65)
      ) {
        return 'You can select a minimum age between 18 and 25, and a maximum age of 65 or above.';
      }
    }

    if (activeStep === 'Budget') {
      const budgetError = renderBudgetError();

      if (!!budgetError) {
        return budgetError;
      }
    }

    if (activeStep === 'Billing Summary') {
      if (
        !formValues?.paymentMethod &&
        (location || brand)?.allowSetupPayment
      ) {
        return 'Please select a payment method';
      }
    }
  };

  const handleNext = () => {
    const errorMsg = renderError();

    if (!!errorMsg) {
      return dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    }

    const step = steps?.find(
      (value: string, index: number) => index === activeStepIndex + 1,
    );
    setActiveStep(step);
    setActiveStepIndex((prev) => prev + 1);

    setProgress((prevProgress) => prevProgress + (1 / steps.length) * 100);
  };

  const handleBack = () => {
    const step = steps?.find((value: string, index: number) => {
      return index === activeStepIndex - 1;
    });
    setActiveStep(step);
    setActiveStepIndex((prev) => prev - 1);
    setProgress((prevProgress) => prevProgress - (1 / steps.length) * 100);
  };

  const getSuccessMessage = () => {
    if (isEdit) {
      return 'Campaign updated successfully';
    }

    if (isAbTest) {
      return 'Ad variant created successfully';
    }

    return 'Campaign created successfully';
  };

  const handleCloseTemplateForm = () => {
    setOpenTemplateForm(false);
    setTemplateAction('create');
  };

  const buildPageheader = () => {
    let title: string = '';
    switch (activeStep) {
      case 'Templates':
        title = 'PLEASE CHOOSE YOUR TEMPLATE';
        break;
      case 'Geolocations':
        title = 'PLEASE CHOOSE YOUR TARGET LOCATIONS';
        break;
      case 'Audiences':
        title = 'PLEASE CHOOSE YOUR TARGET AUDIENCES';
        break;
      case 'Demographics':
        title = 'PLEASE CHOOSE YOUR TARGET DEMOGRAPHICS';
        break;
      case 'Budget':
        title = 'PLEASE SET YOUR SCHEDULE AND BUDGET';
        break;
      case 'Ad Preview':
        title = 'PLEASE SELECT YOUR LEAD AD TOOL';
        break;
      case 'Campaign Details':
        title = 'CAMPAIGN DETAILS';
        break;
      default:
        title = 'BILLING SUMMARY';
        break;
    }

    return title;
  };

  const handleOnShowMobilePreview = () => {
    setShowMobilePreview((prev) => !prev);
  };

  const handleCloseGeolocationSetForm = () => {
    setOpenGeolocationSetForm(false);
    setGeolocationSetAction('create');
  };

  const handleCloseAudienceSetForm = () => {
    setOpenAudienceSetForm(false);
    setAudienceSetAction('create');
  };

  const handleCloseDemographicsSetForm = () => {
    setOpenDemographicsSetForm(false);
    setDemographicsSetAction('create');
  };

  const handleSelectGeolocationSet = (geolocationSet: IGeolocationSet) => {
    let geolocations: any = {
      geo_locations: geolocationSet?.geolocations,
      excluded_geo_locations: geolocationSet?.excludedGeolocations,
    };

    setFormValues({
      ...formValues,
      adSetTemplate: {
        ...formValues?.adSetTemplate,
        targeting: {
          ...formValues?.adSetTemplate?.targeting,
          ...geolocations,
        },
        geolocationSet: geolocationSet._id,
      },
    });
    setSelectedGeolocationSet(geolocationSet);
  };

  const handleSelectAudienceSet = (audienceSet: AudienceSet) => {
    const targets = parsedDetailedTargeting(audienceSet?.targets);

    let tempTargets: FbTargetSearch = {
      ...targets,
      geo_locations: formValues?.adSetTemplate?.targeting?.geo_locations || {},
      excluded_geo_locations:
        formValues?.adSetTemplate?.targeting?.excluded_geo_locations || {},
      age_max: formValues?.adSetTemplate?.targeting?.age_max,
      age_min: formValues?.adSetTemplate?.targeting?.age_min,
    };

    setFormValues({
      ...formValues,
      adSetTemplate: {
        ...formValues?.adSetTemplate,
        targeting: tempTargets,
        audienceSet: audienceSet._id,
      },
    });

    setSelectedAudienceSet(audienceSet);
  };

  const handleSelectDemographicsSet = (demographicsSet: DemographicsSet) => {
    setFormValues({
      ...formValues,
      adSetTemplate: {
        ...formValues?.adSetTemplate,
        targeting: {
          ...formValues?.adSetTemplate?.targeting,
          genders: demographicsSet.genders,
          age_min: demographicsSet.age_min,
          age_max: demographicsSet.age_max,
        },
        demographicsSet: demographicsSet._id,
      },
    });
    setSelectedDemographicsSet(demographicsSet);
  };

  const handleOnClearDemographicsSet = () => {
    setFormValues({
      ...formValues,
      adSetTemplate: {
        ...formValues?.adSetTemplate,
        targeting: {
          ...formValues?.adSetTemplate?.targeting,
          genders: [],
          age_min: null,
          age_max: null,
        },
        demographicsSet: null,
      },
    });
    setSelectedDemographicsSet(null);
  };

  const showNavigationButton = () => {
    if (activeStep === 'Geolocations') {
      return !reachLoading && locationSetInitialLoad;
    }

    if (activeStep === 'Audiences') {
      return !reachLoading && audienceSetInitialLoad;
    }

    if (activeStep === 'Demographics') {
      return !reachLoading && demographicsSetInitialLoad;
    }

    return true;
  };

  const handleSelectTemplate = (template: AdsTemplate) => {
    let temp: AdsTemplate = { ...template };

    let originalString: string =
      temp?.creativeTemplate?.asset_feed_spec?.bodies[0]?.text;

    setParsedBody(originalString);

    let parsedCustomField: any[] = [];
    temp.customFields.forEach((custom: any) => {
      let params: any = { ...custom };

      switch (params.field) {
        case '{{address}}':
          params = { ...params, value: buildBrandAddress(brand) };
          originalString = originalString.replace(
            params.field,
            buildBrandAddress(brand),
          );
          break;
        case '{{phone_number}}':
          params = {
            ...params,
            value: brand?.phoneNumber || '',
          };
          originalString = originalString.replace(
            params.field,
            brand?.phoneNumber || '',
          );
          break;
        default:
          params = { ...params, value: params.field };
          originalString = originalString.replace(params.field, params.field);
          break;
      }

      parsedCustomField = [...parsedCustomField, params];
    });

    temp = { ...temp, customFields: parsedCustomField };
    temp.creativeTemplate.asset_feed_spec.bodies[0].text = originalString;

    setSelectedTemplate(temp);
    setFormValues({
      ...formValues,
      creativeTemplate: {
        ...formValues?.creativeTemplate,
        asset_feed_spec: {
          bodies: temp.creativeTemplate.asset_feed_spec.bodies,
          call_to_action_types:
            temp.creativeTemplate.asset_feed_spec.call_to_action_types,
          titles: temp.creativeTemplate.asset_feed_spec.titles,
          descriptions: temp.creativeTemplate.asset_feed_spec.descriptions,
          link_urls: isEdit
            ? formValues?.creativeTemplate?.asset_feed_spec?.link_urls
            : [
                {
                  website_url: diySettings?.defaultUrl,
                  display_url: diySettings?.defaultUrl,
                },
              ],
          ad_formats: temp.creativeTemplate.asset_feed_spec.ad_formats,
          asset_customization_rules:
            temp.creativeTemplate.asset_feed_spec.asset_customization_rules,
          images:
            temp?.media === 'image'
              ? temp.creativeTemplate.asset_feed_spec.images
              : [],
          videos:
            temp?.media === 'video'
              ? temp.creativeTemplate.asset_feed_spec.videos
              : [],
          call_to_actions: [
            {
              type: temp.creativeTemplate.asset_feed_spec.call_to_actions[0]
                .type,
              adlabels: [{ name: 'cta_1' }],
              value: {
                link:
                  formValues?.campaignTemplate?.objective ===
                  Objectives.OUTCOME_TRAFFIC
                    ? isEdit
                      ? formValues?.creativeTemplate?.asset_feed_spec
                          ?.call_to_actions?.[0]?.value?.link
                      : diySettings?.defaultUrl
                    : null,
                lead_gen_form_id:
                  formValues?.campaignTemplate?.objective ===
                  Objectives.OUTCOME_LEADS
                    ? isEdit
                      ? formValues?.creativeTemplate?.asset_feed_spec
                          ?.call_to_actions?.[0]?.value?.lead_gen_form_id
                      : null
                    : null,
              },
            },
          ],
        },
      },
      adTemplate: {
        ...formValues?.adTemplate,
        template: temp._id,
        tag: temp.name,
        description: temp.description,
        media: temp.media,
      },
    });
  };

  const renderContent = () => {
    if (activeStep === 'Templates') {
      return (
        <DiyTemplateForm
          brand={brand}
          templates={templates}
          filteredTemplates={filteredTemplates}
          setFilteredTemplates={setFilteredTemplates}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          diySettings={diySettings}
          adVideo={adVideo}
          onShowMobilePreview={handleOnShowMobilePreview}
          adVideoLoading={adVideoLoading}
          formValues={formValues}
          isEdit={isEdit}
          openTemplateForm={openTemplateForm}
          setOpenTemplateForm={setOpenTemplateForm}
          templateAction={templateAction}
          setTemplateAction={setTemplateAction}
          loading={loading}
          onSelectTemplate={handleSelectTemplate}
        />
      );
    }

    if (activeStep === 'Geolocations') {
      return (
        <DiyGeolocationSetForm
          geolocationSets={geolocationSets}
          filteredGeolocationSets={filteredGeolocationSets}
          setFilteredGeolocationSets={setFilteredGeolocationSets}
          onSelectGeolocationSet={handleSelectGeolocationSet}
          selectedGeolocationSet={selectedGeolocationSet}
          setSelectedGeolocationSet={setSelectedGeolocationSet}
          reachError={reachError}
          reachLoading={reachLoading}
          reachEstimate={reachEstimate}
          setOpenGeolocationSetForm={setOpenGeolocationSetForm}
          setGeolocationSetAction={setGeolocationSetAction}
          loading={geolocationSetsLoading}
        />
      );
    }

    if (activeStep === 'Audiences') {
      return (
        <DiyAudienceSetForm
          audienceSets={audienceSets}
          filteredAudienceSets={filteredAudienceSets}
          setFilteredAudienceSets={setFilteredAudienceSets}
          onSelectAudienceSet={handleSelectAudienceSet}
          selectedAudienceSet={selectedAudienceSet}
          setSelectedAudienceSet={setSelectedAudienceSet}
          reachError={reachError}
          reachLoading={reachLoading}
          reachEstimate={reachEstimate}
          setOpenAudienceSetForm={setOpenAudienceSetForm}
          setAudienceSetAction={setAudienceSetAction}
          loading={audienceSetsLoading}
        />
      );
    }

    if (activeStep === 'Demographics') {
      return (
        <DiyDemographicsSetForm
          selectedAudienceSet={selectedAudienceSet}
          demographicsSets={demographicsSets}
          filteredDemographicsSets={filteredDemographicsSets}
          setFilteredDemographicsSets={setFilteredDemographicsSets}
          onSelectDemographicsSet={handleSelectDemographicsSet}
          selectedDemographicsSet={selectedDemographicsSet}
          setSelectedDemographicsSet={setSelectedDemographicsSet}
          reachError={reachError}
          reachLoading={reachLoading}
          reachEstimate={reachEstimate}
          setOpenDemographicsSetForm={setOpenDemographicsSetForm}
          setDemographicsSetAction={setDemographicsSetAction}
          fetchLoading={loading}
        />
      );
    }

    if (activeStep === 'Ad Preview') {
      return (
        <DiyAdPreviewV2
          brand={location || brand}
          formValues={formValues}
          setFormValues={setFormValues}
          diySettings={diySettings}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          adVideo={adVideo}
          isEdit={isEdit}
          isAbTest={isAbTest}
          parsedBody={parsedBody}
          register={register}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
        />
      );
    }

    if (activeStep === 'Campaign Details') {
      return (
        <DiyAdDetailsFormV2
          brand={location || brand}
          formValues={formValues}
          setFormValues={setFormValues}
          campaigns={campaigns}
          isEdit={isEdit}
          isAbTest={isAbTest}
          adId={adId}
          setSelectedCampaign={setSelectedCampaign}
          register={register}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
        />
      );
    }

    if (activeStep === 'Budget') {
      return (
        <DiyBudgetFormV2
          brand={location || brand}
          formValues={formValues}
          setFormValues={setFormValues}
          timezone={timezone}
          isEdit={isEdit}
          ad={ad}
          currentMonthlyBudget={!isRestart ? currentMonthlyBudget : 0}
          selectedCampaign={selectedCampaign}
          isRestart={isRestart}
        />
      );
    }

    if (activeStep === 'Billing Summary') {
      return (
        <DiyBillingSummaryV2
          brand={brand}
          location={location}
          formValues={formValues}
          setFormValues={setFormValues}
          currentMonthlyBudget={!isRestart ? currentMonthlyBudget : 0}
          confirmPayment={confirmPayment}
          setConfirmPayment={setConfirmPayment}
          selectedCampaign={selectedCampaign}
        />
      );
    }
  };

  const renderDiyForms = () => {
    if (openTemplateForm) {
      return (
        <TemplateForm
          brand={location || brand}
          isEdit={templateAction === 'edit'}
          isDuplicate={templateAction === 'duplicate'}
          templateId={selectedTemplate?._id}
          templates={templates}
          setTemplates={setTemplates}
          onClose={handleCloseTemplateForm}
          isCreateAds
          setFilteredTemplates={setFilteredTemplates}
          onSelectTemplate={handleSelectTemplate}
        />
      );
    } else if (openGeolocationSetForm) {
      return (
        <Box
          className={`${styles.form} ${xsOnly ? `${styles['-mobile']}` : ''}`}
        >
          <GeolocationSetForm
            brand={location || brand}
            onClose={handleCloseGeolocationSetForm}
            edit={geolocationSetAction === 'edit'}
            locationSetId={selectedGeolocationSet?._id}
            isCreateAds
            geolocationSets={geolocationSets}
            setGeolocationSets={setGeolocationSets}
            setFilteredGeolocationSets={setFilteredGeolocationSets}
            onSelectGeolocationSet={handleSelectGeolocationSet}
          />
        </Box>
      );
    } else if (openAudienceSetForm) {
      return (
        <Box
          className={`${styles.form} ${xsOnly ? `${styles['-mobile']}` : ''}`}
        >
          <AudienceSetForm
            brand={location || brand}
            diySettings={diySettings}
            onClose={handleCloseAudienceSetForm}
            edit={audienceSetAction === 'edit'}
            audienceSetId={selectedAudienceSet?._id}
            isCreateAds
            audienceSets={audienceSets}
            setAudienceSets={setAudienceSets}
            setFilteredAudienceSets={setFilteredAudienceSets}
            onSelectAudienceSet={handleSelectAudienceSet}
          />
        </Box>
      );
    } else if (openDemographicsSetForm) {
      return (
        <Box
          className={`${styles.form} ${xsOnly ? `${styles['-mobile']}` : ''}`}
        >
          <DemographicsSetForm
            brand={location || brand}
            onClose={handleCloseDemographicsSetForm}
            edit={demographicsSetAction === 'edit'}
            demographicsSetId={selectedDemographicsSet?._id}
            isCreateAds
            demographicsSets={demographicsSets}
            setDemographicsSets={setDemographicsSets}
            setFilteredDemographicsSets={setFilteredDemographicsSets}
            onSelectDemographicsSet={handleSelectDemographicsSet}
            onClearDemographicsSet={handleOnClearDemographicsSet}
            selectedAudienceSet={selectedAudienceSet}
          />
        </Box>
      );
    }
  };

  const scrollStyling = {
    minHeight: `calc(100vh - 100px)`,
    maxHeight: `calc(100vh - 100px)`,
    overflowY: 'auto',
    ...ScrollStyle(),
  };

  return openTemplateForm ||
    openGeolocationSetForm ||
    openAudienceSetForm ||
    openDemographicsSetForm ? (
    <Box sx={scrollStyling}>{renderDiyForms()}</Box>
  ) : (
    <Box
      className={`${styles.form} ${xsOnly ? `${styles['-mobile']}` : ''}`}
      sx={
        ['Templates', 'Geolocations', 'Audiences', 'Demographics'].includes(
          activeStep,
        )
          ? {
              minHeight: `calc(100vh - 100px)`,
              maxHeight: `calc(100vh - 100px)`,
            }
          : scrollStyling
      }
    >
      <CircularLoading loading={fetchLoading || rebuildLoading} />

      <Box
        component="div"
        sx={{
          justifyContent: 'space-between',
          display: 'flex',
          alignItems: 'center',
          margin: 0,
          marginBottom: '20px',
        }}
      >
        <Box component="div" sx={{ paddingBottom: 0 }}>
          <PageHeader
            title={buildPageheader()}
            textAlign="left"
            marginBottom="0"
          />
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ minHeight: 'calc(100vh - 100px)' }}>
        <Grid item xs={12}>
          {renderContent()}
        </Grid>
      </Grid>

      <Grid
        container
        spacing={1}
        mt={4}
        sx={{
          position: 'sticky',
          bottom: 0,
          right: 0,
          left: 0,
          backgroundColor: '#FFF',
          paddingBottom: '20px',
          zIndex: 999,
          alignItems: 'flex-end',
        }}
      >
        <Grid item xs={12} sm={6}>
          <LinearProgressBarWithLabel value={progress} />
        </Grid>

        {showNavigationButton() ? (
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              justifyContent: 'flex-end',
              display: 'flex',
              paddingY: '20px',
            }}
          >
            <PrimaryButton
              title="Cancel"
              type="button"
              handleOnClick={() => navigate('/adz')}
              marginRight5
              variant="outlined"
            />

            {activeStepIndex > 0 ? (
              <PrimaryButton
                title="Back"
                type="button"
                handleOnClick={handleBack}
                marginRight5
              />
            ) : null}

            {showNextButton() ? (
              <PrimaryButton
                loading={activeStepIndex + 1 === steps.length && loading}
                title={
                  activeStepIndex + 1 === steps.length ? 'Go Live' : 'Next'
                }
                type="button"
                handleOnClick={
                  activeStepIndex + 1 === steps.length
                    ? handleSubmit(handleOnSubmit)
                    : handleSubmit(handleNext)
                }
                startIcon={
                  activeStepIndex + 1 === steps.length ? (
                    <RocketLaunchIcon />
                  ) : null
                }
              />
            ) : null}
          </Grid>
        ) : null}
      </Grid>

      <DrawerMenu
        anchor="right"
        open={showMobilePreview}
        onClose={handleOnShowMobilePreview}
      >
        <Grid container spacing={2} py={2}>
          <Grid
            item
            xs={12}
            sx={{ justifyContent: 'flex-end', display: 'flex' }}
          >
            <IconButton onClick={handleOnShowMobilePreview}>
              <CloseOutlinedIcon />
            </IconButton>
          </Grid>

          <Grid item xs={12}>
            <ModalHeader title="Ad Preview" />
          </Grid>

          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <AdsPreviewSquare
              page={diySettings?.facebookPage}
              body={
                formValues?.creativeTemplate?.asset_feed_spec?.bodies[0]?.text
              }
              isImage={selectedTemplate?.media === 'image'}
              imageUrl={selectedTemplate?.images?.square}
              websiteUrl={
                formValues?.creativeTemplate?.asset_feed_spec?.link_urls[0]
                  ?.website_url
              }
              headline={
                formValues?.creativeTemplate?.asset_feed_spec?.titles[0]?.text
              }
              description={
                formValues?.creativeTemplate?.asset_feed_spec?.descriptions[0]
                  ?.text
              }
              cta={
                formValues?.creativeTemplate?.asset_feed_spec
                  ?.call_to_action_types[0]
              }
              videoUrl={adVideo?.source}
              objective={formValues?.campaignTemplate?.objective}
              brand={brand}
            />
          </Grid>
        </Grid>
      </DrawerMenu>
    </Box>
  );
};

export default DiyFormV2;
